import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Copy, KeyRound, RefreshCw, ShieldCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import AdminLayout from '@/components/admin/AdminLayout';
import { AdminEmptyState, AdminMetricCard, AdminPanel } from '@/components/admin/AdminSurface';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  createMachineApiKey,
  createMachinePrincipal,
  deleteMachineApiKey,
  listMachineAccess,
  revokeMachineApiKey,
} from '@/services/api/adminMachineAccessApi';
import type { AdminScope } from '@/types/api';
import { cn } from '@/lib/utils';

const SUPPORT_PRINCIPAL_NAME = 'support-agent';
const SUPPORT_KEY_NAME = 'support-primary';
const ALL_SUPPORT_SCOPES: AdminScope[] = [
  'admin:listings:read',
  'admin:listings:write',
  'admin:reviews:read',
  'admin:reviews:write',
] as const;
const DEFAULT_SUPPORT_SCOPES: AdminScope[] = [
  'admin:listings:read',
  'admin:reviews:read',
];

const SCOPE_OPTIONS: Array<{
  scope: AdminScope;
  label: string;
  description: string;
}> = [
  {
    scope: 'admin:listings:read',
    label: 'Listings read',
    description: 'Lets the key inspect listing moderation queues and listing details.',
  },
  {
    scope: 'admin:listings:write',
    label: 'Listings write',
    description: 'Lets the key approve, reject, or update listings.',
  },
  {
    scope: 'admin:reviews:read',
    label: 'Reviews read',
    description: 'Lets the key inspect reported reviews and moderation state.',
  },
  {
    scope: 'admin:reviews:write',
    label: 'Reviews write',
    description: 'Lets the key hide, resolve, or delete reviews.',
  },
];

const READ_SCOPE_BY_WRITE_SCOPE: Partial<Record<AdminScope, AdminScope>> = {
  'admin:listings:write': 'admin:listings:read',
  'admin:reviews:write': 'admin:reviews:read',
};

const WRITE_SCOPE_BY_READ_SCOPE: Partial<Record<AdminScope, AdminScope>> = {
  'admin:listings:read': 'admin:listings:write',
  'admin:reviews:read': 'admin:reviews:write',
};

const SECURITY_CHECKLIST = [
  {
    title: 'Header-only authentication',
    description: 'The support key authenticates through the x-api-key header only.',
  },
  {
    title: 'Browser login unchanged',
    description: 'Issuing or revoking this key does not affect the user-facing login flow.',
  },
  {
    title: 'Write actions are audited',
    description: 'Admin write operations performed with the key continue to generate audit events.',
  },
  {
    title: 'Plaintext is one-time only',
    description: 'Only the key prefix is queryable later. The full key stays hashed at rest.',
  },
];

const formatDateTime = (value: string | null) => {
  if (!value) {
    return 'Never';
  }

  return new Date(value).toLocaleString();
};

const SupportApiKeyPage = () => {
  const queryClient = useQueryClient();
  const [plaintextApiKey, setPlaintextApiKey] = useState<string | null>(null);
  const [selectedScopes, setSelectedScopes] = useState<AdminScope[]>(DEFAULT_SUPPORT_SCOPES);

  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['admin-machine-access'],
    queryFn: listMachineAccess,
  });

  const supportPrincipal = useMemo(
    () => data?.principals.find((principal) => principal.name === SUPPORT_PRINCIPAL_NAME) ?? null,
    [data?.principals]
  );

  const supportKeys = useMemo(
    () => (data?.apiKeys ?? []).filter((apiKey) => apiKey.machinePrincipalId === supportPrincipal?.id),
    [data?.apiKeys, supportPrincipal?.id]
  );

  const activeKey = useMemo(
    () => supportKeys.find((apiKey) => !apiKey.revokedAt) ?? null,
    [supportKeys]
  );

  const orderedSupportKeys = useMemo(
    () =>
      [...supportKeys].sort((left, right) => {
        const leftRevoked = Boolean(left.revokedAt);
        const rightRevoked = Boolean(right.revokedAt);

        if (leftRevoked !== rightRevoked) {
          return leftRevoked ? 1 : -1;
        }

        return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
      }),
    [supportKeys]
  );

  const revokedKeyCount = useMemo(
    () => supportKeys.filter((apiKey) => Boolean(apiKey.revokedAt)).length,
    [supportKeys]
  );

  const refreshMachineAccess = async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin-machine-access'] });
  };

  const issueKeyMutation = useMutation({
    mutationFn: async () => {
      let principalId = supportPrincipal?.id ?? null;

      if (!principalId) {
        const principalResponse = await createMachinePrincipal({
          name: SUPPORT_PRINCIPAL_NAME,
          description: 'Support-only principal for admin API automation',
          allowedScopes: [...ALL_SUPPORT_SCOPES],
        });
        principalId = principalResponse.principal.id;
      }

      return createMachineApiKey({
        machinePrincipalId: principalId,
        name: SUPPORT_KEY_NAME,
        allowedScopes: selectedScopes,
      });
    },
    onSuccess: async (response) => {
      setPlaintextApiKey(response.plaintextApiKey);
      await refreshMachineAccess();
      toast.success('Support API key issued');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to issue support API key');
    },
  });

  const revokeKeyMutation = useMutation({
    mutationFn: (apiKeyId: string) => revokeMachineApiKey(apiKeyId),
    onSuccess: async () => {
      setPlaintextApiKey(null);
      await refreshMachineAccess();
      toast.success('Support API key revoked');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to revoke support API key');
    },
  });

  const deleteKeyMutation = useMutation({
    mutationFn: (apiKeyId: string) => deleteMachineApiKey(apiKeyId),
    onSuccess: async () => {
      await refreshMachineAccess();
      toast.success('Revoked API key deleted');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete revoked API key');
    },
  });

  const handleCopy = async () => {
    if (!plaintextApiKey) {
      return;
    }

    try {
      await navigator.clipboard.writeText(plaintextApiKey);
      toast.success('API key copied');
    } catch {
      toast.error('Failed to copy API key');
    }
  };

  const handleScopeChange = (scope: AdminScope, checked: boolean) => {
    setSelectedScopes((currentScopes) => {
      const nextScopes = new Set<AdminScope>(currentScopes);

      if (checked) {
        nextScopes.add(scope);

        const pairedReadScope = READ_SCOPE_BY_WRITE_SCOPE[scope];
        if (pairedReadScope) {
          nextScopes.add(pairedReadScope);
        }
      } else {
        nextScopes.delete(scope);

        const pairedWriteScope = WRITE_SCOPE_BY_READ_SCOPE[scope];
        if (pairedWriteScope) {
          nextScopes.delete(pairedWriteScope);
        }
      }

      return ALL_SUPPORT_SCOPES.filter((candidateScope) => nextScopes.has(candidateScope));
    });
  };

  const hasWriteAccessSelected = selectedScopes.some((scope) => scope.endsWith(':write'));
  const selectedReadCount = selectedScopes.filter((scope) => scope.endsWith(':read')).length;
  const selectedWriteCount = selectedScopes.filter((scope) => scope.endsWith(':write')).length;
  const activeKeyHasWriteAccess = Boolean(
    activeKey?.allowedScopes.some((scope) => scope.endsWith(':write'))
  );
  const issueBlockedReason =
    selectedScopes.length === 0
      ? 'Select at least one scope before issuing a key.'
      : activeKey
        ? 'Revoke the active key before issuing a replacement.'
        : 'The new key will be shown once immediately after it is issued.';

  return (
    <AdminLayout
      title="Support API Key"
      description="Issue, rotate, and review the single support machine key with the active state, selected scope level, and key history visible in one place."
      actions={
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isRefetching}
          className="rounded-lg border-gray-200 text-gray-700 hover:border-whatsapp hover:text-whatsapp"
        >
          <RefreshCw className={cn('h-4 w-4', isRefetching && 'animate-spin')} />
          {isRefetching ? 'Refreshing...' : 'Refresh'}
        </Button>
      }
    >
      <div className="space-y-6">
        {plaintextApiKey && (
          <Alert className="border-whatsapp/20 bg-whatsapp-light/50 text-gray-800">
            <CheckCircle2 className="h-4 w-4 text-whatsapp" />
            <AlertTitle>Copy the new key now</AlertTitle>
            <AlertDescription className="space-y-3">
              <p>
                The plaintext support key is shown only once. It will not be retrievable again after you leave
                or refresh this page.
              </p>
              <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
                <div className="rounded-lg border border-whatsapp/20 bg-white px-4 py-3 font-mono text-sm break-all">
                  {plaintextApiKey}
                </div>
                <Button
                  onClick={handleCopy}
                  size="sm"
                  className="w-full rounded-lg bg-whatsapp text-white hover:bg-whatsapp-dark lg:w-auto"
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy key
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        <AdminPanel
          title="Current State"
          description="The page keeps principal readiness, key status, access level, and key history visible before you issue or rotate anything."
        >
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <AdminMetricCard
              label="Principal"
              value={supportPrincipal ? 'Ready' : 'Not created'}
              description={supportPrincipal ? supportPrincipal.name : 'Created automatically on first issue'}
              icon={ShieldCheck}
              tone={supportPrincipal ? 'accent' : 'neutral'}
            />
            <AdminMetricCard
              label="Active key"
              value={activeKey ? '1 active' : 'None'}
              description={activeKey ? `Last used ${formatDateTime(activeKey.lastUsedAt)}` : 'Safe to issue a new key now'}
              icon={KeyRound}
              tone={activeKey ? 'accent' : 'neutral'}
            />
            <AdminMetricCard
              label="Next key access"
              value={hasWriteAccessSelected ? 'Write enabled' : 'Read-only'}
              description={
                hasWriteAccessSelected
                  ? `${selectedWriteCount} write scope${selectedWriteCount === 1 ? '' : 's'} selected`
                  : `${selectedReadCount} read scope${selectedReadCount === 1 ? '' : 's'} selected`
              }
              icon={CheckCircle2}
              tone={hasWriteAccessSelected ? 'warning' : 'success'}
            />
            <AdminMetricCard
              label="Issued keys"
              value={supportKeys.length}
              description={revokedKeyCount > 0 ? `${revokedKeyCount} revoked key${revokedKeyCount === 1 ? '' : 's'}` : 'No revoked keys'}
              icon={RefreshCw}
              tone={supportKeys.length > 0 ? 'neutral' : 'accent'}
            />
          </div>
        </AdminPanel>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
          <AdminPanel
            title="Issue Or Rotate Key"
            description="New keys default to read-only. Add write scopes only when the support agent needs to make changes."
            actions={
              <Badge variant="outline" className="border-gray-200 bg-gray-100 text-gray-600">
                Single active key
              </Badge>
            }
          >
            <div className="space-y-5">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-medium uppercase tracking-[0.14em] text-gray-500">
                    Support principal
                  </p>
                  <p className="mt-2 text-sm font-semibold text-gray-900">
                    {supportPrincipal ? supportPrincipal.name : SUPPORT_PRINCIPAL_NAME}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    {supportPrincipal
                      ? 'Ready for rotation and new key issuance.'
                      : 'Created automatically the first time you issue a support key.'}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-xs font-medium uppercase tracking-[0.14em] text-gray-500">
                    Selected scopes
                  </p>
                  <p className="mt-2 text-sm font-semibold text-gray-900">
                    {selectedScopes.length === 0
                      ? 'No access selected'
                      : `${selectedScopes.length} scope${selectedScopes.length === 1 ? '' : 's'} selected`}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    {hasWriteAccessSelected
                      ? `${selectedReadCount} read and ${selectedWriteCount} write scope${selectedWriteCount === 1 ? '' : 's'}`
                      : `${selectedReadCount} read-only scope${selectedReadCount === 1 ? '' : 's'}`}
                  </p>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                {SCOPE_OPTIONS.map(({ scope, label, description }) => {
                  const checked = selectedScopes.includes(scope);
                  const isWriteScope = scope.endsWith(':write');

                  return (
                    <label
                      key={scope}
                      className={cn(
                        'flex h-full items-start gap-3 rounded-lg border px-4 py-4 transition-colors',
                        checked
                          ? 'border-whatsapp/30 bg-whatsapp-light/40'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                      )}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(value) => handleScopeChange(scope, value === true)}
                        className="mt-0.5"
                      />
                      <div className="min-w-0 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Label className="cursor-pointer text-sm font-medium text-gray-900">{label}</Label>
                          <Badge
                            variant="outline"
                            className={
                              isWriteScope
                                ? 'border-amber-200 bg-amber-50 text-amber-700'
                                : 'border-gray-200 bg-gray-100 text-gray-600'
                            }
                          >
                            {isWriteScope ? 'Write' : 'Read'}
                          </Badge>
                        </div>
                        <p className="text-sm leading-6 text-gray-500">{description}</p>
                        <p className="font-mono text-xs text-gray-500">{scope}</p>
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-4">
                <p className="text-xs font-medium uppercase tracking-[0.14em] text-gray-500">
                  Next key permissions
                </p>
                {selectedScopes.length > 0 ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {selectedScopes.map((scope) => (
                      <Badge
                        key={scope}
                        variant="outline"
                        className="border-gray-200 bg-white text-gray-700"
                      >
                        {scope}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-gray-500">Select at least one scope to enable issuance.</p>
                )}
                <p className="mt-3 text-sm text-gray-500">
                  Selecting a write scope automatically keeps its matching read scope enabled.
                </p>
              </div>

              <Alert className={hasWriteAccessSelected ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-gray-50'}>
                <ShieldCheck className={cn('h-4 w-4', hasWriteAccessSelected ? 'text-amber-700' : 'text-gray-600')} />
                <AlertTitle className="text-gray-900">
                  {hasWriteAccessSelected ? 'Write access selected' : 'Read-only selected'}
                </AlertTitle>
                <AlertDescription className="text-gray-600">
                  {hasWriteAccessSelected
                    ? 'This key will be able to perform listing or review moderation actions for the write scopes selected above.'
                    : 'This key can inspect state only. It will not be able to approve, reject, hide, resolve, or delete records.'}
                </AlertDescription>
              </Alert>

              <div className="flex flex-col gap-4 border-t border-gray-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-gray-900">{issueBlockedReason}</p>
                  <p className="text-sm text-gray-500">
                    Issuing a new key does not change browser authentication or user accounts.
                  </p>
                </div>
                <Button
                  onClick={() => issueKeyMutation.mutate()}
                  disabled={Boolean(activeKey) || issueKeyMutation.isPending || isLoading || selectedScopes.length === 0}
                  className="w-full rounded-lg bg-whatsapp text-white hover:bg-whatsapp-dark sm:w-auto"
                >
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  {issueKeyMutation.isPending ? 'Issuing key...' : 'Issue support API key'}
                </Button>
              </div>
            </div>
          </AdminPanel>

          <div className="space-y-6">
            <AdminPanel
              title="Active Key"
              description="The single live support key sits here so rotation decisions do not require digging through key history."
            >
              {activeKey ? (
                <div className="space-y-4">
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0 space-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-semibold text-gray-900">{activeKey.name}</p>
                          <Badge className="border-whatsapp/20 bg-whatsapp-light text-whatsapp">Active</Badge>
                          <Badge
                            variant="outline"
                            className={
                              activeKeyHasWriteAccess
                                ? 'border-amber-200 bg-amber-50 text-amber-700'
                                : 'border-gray-200 bg-white text-gray-600'
                            }
                          >
                            {activeKeyHasWriteAccess ? 'Write enabled' : 'Read-only'}
                          </Badge>
                        </div>
                        <p className="break-all font-mono text-sm text-gray-500">{activeKey.keyPrefix}</p>
                        <dl className="grid gap-3 sm:grid-cols-2">
                          <div>
                            <dt className="text-xs font-medium uppercase tracking-[0.14em] text-gray-500">Created</dt>
                            <dd className="mt-1 text-sm text-gray-700">{formatDateTime(activeKey.createdAt)}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-medium uppercase tracking-[0.14em] text-gray-500">Last used</dt>
                            <dd className="mt-1 text-sm text-gray-700">{formatDateTime(activeKey.lastUsedAt)}</dd>
                          </div>
                        </dl>
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => revokeKeyMutation.mutate(activeKey.id)}
                        disabled={revokeKeyMutation.isPending || deleteKeyMutation.isPending}
                        className="w-full rounded-lg border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 sm:w-auto"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {revokeKeyMutation.isPending ? 'Revoking...' : 'Revoke active key'}
                      </Button>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.14em] text-gray-500">Allowed scopes</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {activeKey.allowedScopes.map((scope) => (
                        <Badge
                          key={scope}
                          variant="outline"
                          className="border-gray-200 bg-white text-gray-700"
                        >
                          {scope}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <AdminEmptyState
                  icon={KeyRound}
                  title="No active support key"
                  description="You can issue a new support key as soon as you have the scopes selected on the left."
                  className="min-h-[180px]"
                />
              )}
            </AdminPanel>

            <AdminPanel
              title="Security Checklist"
              description="The support key is scoped to the admin API boundary and can be reviewed quickly before you issue or rotate it."
            >
              <div className="grid gap-3 sm:grid-cols-2">
                {SECURITY_CHECKLIST.map((item) => (
                  <div key={item.title} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                    <p className="mt-1 text-sm leading-6 text-gray-500">{item.description}</p>
                  </div>
                ))}
              </div>
            </AdminPanel>
          </div>
        </div>

        <AdminPanel
          title="Issued Keys"
          description="History stays below the live controls so you can review previous keys without losing your place in the rotation workflow."
        >
          <div>
            {isLoading ? (
              <div className="grid gap-3">
                {Array.from({ length: 2 }).map((_, index) => (
                  <div key={index} className="h-36 animate-pulse rounded-lg border border-gray-200 bg-gray-50" />
                ))}
              </div>
            ) : orderedSupportKeys.length === 0 ? (
              <AdminEmptyState
                icon={KeyRound}
                title="No support keys issued yet"
                description="Once a support key is created, its prefix, dates, and scopes will appear here."
                className="min-h-[200px]"
              />
            ) : (
              <div className="space-y-4">
                {orderedSupportKeys.map((apiKey) => {
                  const isRevoked = Boolean(apiKey.revokedAt);
                  const hasWriteAccess = apiKey.allowedScopes.some((scope) => scope.endsWith(':write'));

                  return (
                    <div key={apiKey.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0 space-y-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-semibold text-gray-900">{apiKey.name}</span>
                            <Badge
                              className={
                                isRevoked
                                  ? 'border-gray-200 bg-gray-100 text-gray-600'
                                  : 'border-whatsapp/20 bg-whatsapp-light text-whatsapp'
                              }
                            >
                              {isRevoked ? 'Revoked' : 'Active'}
                            </Badge>
                            <Badge
                              variant="outline"
                              className={
                                hasWriteAccess
                                  ? 'border-amber-200 bg-amber-50 text-amber-700'
                                  : 'border-gray-200 bg-white text-gray-600'
                              }
                            >
                              {hasWriteAccess ? 'Write enabled' : 'Read-only'}
                            </Badge>
                          </div>

                          <p className="break-all font-mono text-sm text-gray-500">{apiKey.keyPrefix}</p>

                          <dl className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                            <div>
                              <dt className="text-xs font-medium uppercase tracking-[0.14em] text-gray-500">Created</dt>
                              <dd className="mt-1 text-sm text-gray-700">{formatDateTime(apiKey.createdAt)}</dd>
                            </div>
                            <div>
                              <dt className="text-xs font-medium uppercase tracking-[0.14em] text-gray-500">Last used</dt>
                              <dd className="mt-1 text-sm text-gray-700">{formatDateTime(apiKey.lastUsedAt)}</dd>
                            </div>
                            <div>
                              <dt className="text-xs font-medium uppercase tracking-[0.14em] text-gray-500">Revoked</dt>
                              <dd className="mt-1 text-sm text-gray-700">{formatDateTime(apiKey.revokedAt)}</dd>
                            </div>
                          </dl>

                          <div className="flex flex-wrap gap-2">
                            {apiKey.allowedScopes.map((scope) => (
                              <Badge
                                key={scope}
                                variant="outline"
                                className="border-gray-200 bg-gray-50 text-gray-700"
                              >
                                {scope}
                              </Badge>
                            ))}
                          </div>
                        </div>

                        {!isRevoked ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => revokeKeyMutation.mutate(apiKey.id)}
                            disabled={revokeKeyMutation.isPending || deleteKeyMutation.isPending}
                            className="w-full rounded-lg border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 sm:w-auto"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {revokeKeyMutation.isPending ? 'Revoking...' : 'Revoke'}
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteKeyMutation.mutate(apiKey.id)}
                            disabled={deleteKeyMutation.isPending || revokeKeyMutation.isPending}
                            className="w-full rounded-lg border-gray-200 text-gray-700 hover:border-whatsapp hover:text-whatsapp sm:w-auto"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {deleteKeyMutation.isPending ? 'Deleting...' : 'Delete'}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </AdminPanel>
      </div>
    </AdminLayout>
  );
};

export default SupportApiKeyPage;
